/**
 * WebApp2 富文本渲染：对齐 Mobile2 `renderRichTextHtml.ts`
 * 管线：repairLatexEscapes → protectMath → marked → KaTeX（行内 $ 优先）
 *
 * 权威说明：
 * - AppFunctor/docs/dictation-blank-box-playbook.md §5
 * - AppFunctor/docs/math-latex-cjk-render-playbook.md §4
 */
import { marked } from 'marked';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { KATEX_RENDER_OPTIONS } from './katexRenderOptions';
import { repairLatexEscapes, splitAdjacentInlineMathDelimiters } from './repairLatexEscapes';
import { isDictationTaggedText } from './dictationTtsText';

const MATH_TOKEN_PREFIX = '%%PFMATH';
const MATH_TOKEN_SUFFIX = '%%';

const LATEX_PATTERN = /(\$\$[\s\S]+?\$\$|\$[^$\n]+\$|\\\(|\\\[|\\(?:binom|frac|sqrt|sum|int|prod|lim|alpha|beta|gamma|delta|theta|pi|sigma|mu|infty|cdot|times|leq|geq|neq|approx|le|ge|ne|in|notin|subset|cup|cap|left|right|begin|end|mathbb|mathbf|mathrm|overline|underline|vec|hat|bar|dot|text|boxed|phantom|vphantom|hspace|rule|fcolorbox|fbox|triangle|tan|to|bigl|bigr)\b|[_^]\{[^}]+\})/;
const MARKDOWN_BLOCK_PATTERN = /(^|\n)(#{1,6}\s|>\s|[-*+]\s|\d+[.)]\s|```)/m;
const MARKDOWN_INLINE_PATTERN = /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|`[^`]+`|\[[^\]]+\]\([^)]+\))/;
const MARKDOWN_TABLE_PATTERN = /(^|\n)\|.+\|\s*\n\|(?:\s*:?-{3,}:?\s*\|)+/m;

export function shouldRenderRichTextBodyHtml(text: string) {
  return LATEX_PATTERN.test(text)
    || MARKDOWN_BLOCK_PATTERN.test(text)
    || MARKDOWN_INLINE_PATTERN.test(text)
    || MARKDOWN_TABLE_PATTERN.test(text)
    || /\\(?:boxed|fcolorbox|fbox)\{/.test(text)
    || isDictationTaggedText(text);
}

function protectMath(source: string, mathTokens: string[]) {
  let protectedText = splitAdjacentInlineMathDelimiters(source);

  const stash = (match: string) => {
    const index = mathTokens.length;
    mathTokens.push(match);
    // 纯 ASCII 哨兵：marked 不会当 HTML/强调拆掉
    return `${MATH_TOKEN_PREFIX}${index}${MATH_TOKEN_SUFFIX}`;
  };

  // 先块级 $$ / \[ \]，再行内 $ / \( \)，避免 $$…$$ 被拆成空的 $…$
  protectedText = protectedText.replace(/\$\$([\s\S]+?)\$\$/g, (match) => stash(match));
  protectedText = protectedText.replace(/\\\[([\s\S]+?)\\\]/g, (match) => stash(match));
  protectedText = protectedText.replace(/\$([^$]+?)\$/g, (match) => stash(match));
  protectedText = protectedText.replace(/\\\(([\s\S]+?)\\\)/g, (match) => stash(match));

  return protectedText;
}

function renderOneMathToken(raw: string): string {
  const token = String(raw || '').trim();
  if (!token) return '';

  const render = (expression: string, displayMode: boolean) => {
    let expr = expression.trim()
      .replace(/\\begin\{align\*?\}/g, '\\begin{aligned}')
      .replace(/\\end\{align\*?\}/g, '\\end{aligned}');
    try {
      return katex.renderToString(expr, {
        ...KATEX_RENDER_OPTIONS,
        displayMode,
        throwOnError: true,
      });
    } catch {
      try {
        return katex.renderToString(expr, {
          ...KATEX_RENDER_OPTIONS,
          displayMode: true,
          throwOnError: true,
        });
      } catch {
        return escapePlainHtml(token);
      }
    }
  };

  if (token.startsWith('$$') && token.endsWith('$$')) {
    return render(token.slice(2, -2), true);
  }
  if (token.startsWith('\\[') && token.endsWith('\\]')) {
    return render(token.slice(2, -2), true);
  }
  if (token.startsWith('\\(') && token.endsWith('\\)')) {
    return render(token.slice(2, -2), false);
  }
  if (token.startsWith('$') && token.endsWith('$')) {
    const inner = token.slice(1, -1);
    // 行内定界符却包了 \begin{aligned} → 必须按 display 渲染，否则露红色源码
    const display = /\\begin\{/.test(inner);
    return render(inner, display);
  }
  return render(token, /\\begin\{/.test(token));
}

function restoreMath(html: string, mathTokens: string[]) {
  // 直接渲染占位符 → KaTeX HTML，避免对已含 HTML 的字符串再扫 $...$
  return String(html || '').replace(
    /%%PFMATH(\d+)%%/g,
    (_, indexRaw) => renderOneMathToken(mathTokens[Number(indexRaw)] || ''),
  );
}

function renderKatexInHtml(html: string) {
  // 兜底：仅处理 restore 后仍残留的裸定界符（不应再扫已渲染的 katex HTML）
  let rendered = html;

  rendered = rendered.replace(/(?<![\\])\$\$([\s\S]+?)\$\$/g, (_, expression: string) => {
    try {
      return katex.renderToString(expression.trim(), { ...KATEX_RENDER_OPTIONS, displayMode: true });
    } catch {
      return escapePlainHtml(`$$${expression}$$`);
    }
  });

  rendered = rendered.replace(/(?<![\\])\$([^$\n]+?)\$/g, (full, expression: string) => {
    // 已是 HTML 片段则跳过
    if (/<span\b/i.test(expression) || /&lt;span/i.test(expression)) {
      return full;
    }
    try {
      return katex.renderToString(expression.trim(), { ...KATEX_RENDER_OPTIONS, displayMode: false });
    } catch {
      return escapePlainHtml(`$${expression}$`);
    }
  });

  return rendered;
}

function escapePlainHtml(input: string) {
  return String(input || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function renderDocumentPlainBodyHtml(sourceText: string) {
  const normalized = String(sourceText || '').trim();
  if (!normalized) return '';
  return `<p>${escapePlainHtml(normalized).replace(/\n/g, '<br/>')}</p>`;
}

export function renderDocumentPlainInlineHtml(sourceText: string) {
  const normalized = String(sourceText || '').trim();
  if (!normalized) return '';
  return `<span class="text-run">${escapePlainHtml(normalized).replace(/\n+/g, ' ')}</span>`;
}

/** Mobile2 同源：protectMath → marked → 占位符直接 KaTeX（不再二次扫 $） */
export function renderRichTextBodyHtml(sourceText: string) {
  const mathTokens: string[] = [];
  const protectedText = protectMath(repairLatexEscapes(sourceText), mathTokens);
  const parsed = marked.parse(protectedText, { async: false, gfm: true, breaks: true });
  return restoreMath(String(parsed || ''), mathTokens);
}

/**
 * 爆炸选段 / 行内公式：禁止 marked 包 `<p>`，否则每个分句都会块级换行。
 * 显式换行折叠为空格，保持自然文气流。
 */
export function renderInlineRichTextBodyHtml(sourceText: string) {
  const mathTokens: string[] = [];
  const protectedText = protectMath(repairLatexEscapes(sourceText), mathTokens);
  const renderer = new marked.Renderer();
  renderer.paragraph = (text) => {
    const body = typeof text === 'string' ? text : String((text as { text?: string })?.text ?? '');
    return body;
  };
  renderer.br = () => ' ';
  const parsed = marked.parse(protectedText, {
    async: false,
    gfm: true,
    breaks: false,
    renderer,
  });
  const flattened = String(parsed || '')
    .replace(/<\/?p>/gi, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const withMath = restoreMath(flattened, mathTokens);
  return withMath;
}

export function renderContentBodyHtml(sourceText: string) {
  const normalized = String(sourceText || '').trim();
  if (!normalized) return '';

  if (!shouldRenderRichTextBodyHtml(normalized)) {
    return `<span class="text-run">${escapePlainHtml(normalized).replace(/\n/g, '<br/>')}</span>`;
  }

  return renderRichTextBodyHtml(normalized);
}

export function renderInlineContentBodyHtml(sourceText: string) {
  const normalized = String(sourceText || '').trim();
  if (!normalized) return '';

  if (!shouldRenderRichTextBodyHtml(normalized)) {
    return renderDocumentPlainInlineHtml(normalized);
  }

  return renderInlineRichTextBodyHtml(normalized);
}
